import type React from "react"
import { Table, Tag, Typography, Button, Tooltip, type TablePaginationConfig, type TableProps } from "antd"
import { EyeOutlined, MailOutlined, WarningOutlined } from "@ant-design/icons"
import { Link } from "react-router-dom"
import type { EmailLogItem } from "@/services/log/emailLogApi"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import dayjs from "dayjs"

const { Text } = Typography

interface EmailLogTableProps {
  data: EmailLogItem[]
  loading: boolean
  onViewDetail: (record: EmailLogItem) => void
  pagination?: TableProps["pagination"]
}

export const EmailLogTable: React.FC<EmailLogTableProps> = ({ data, loading, onViewDetail, pagination }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<EmailLogItem>["columns"] = [
    {
      title: "#ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      align: "center",
      render: (id: number) => <span className="font-mono text-gray-500 text-xs">#{id}</span>,
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      align: "center",
      render: (val: string) => {
        if (!val) return "—"
        const d = dayjs(val)
        return (
          <div className="flex flex-col font-mono text-xs leading-tight">
            <span className="font-medium text-gray-800">{d.format("DD/MM/YYYY")}</span>
            <Text type="secondary" className="text-[11px]">
              {d.format("HH:mm:ss")}
            </Text>
          </div>
        )
      },
    },
    {
      title: "Người nhận (To)",
      dataIndex: "to_address",
      key: "to_address",
      width: 220,
      render: (to: string) => {
        if (!to) return "—"
        return (
          <Tooltip title={`Xem trong DS User: ${to}`}>
            <Link
              to={`/user-account?search=${encodeURIComponent(to)}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 font-medium text-blue-600 hover:underline"
            >
              <MailOutlined className="text-gray-400" />
              <span className="max-w-[190px] truncate">{to}</span>
            </Link>
          </Tooltip>
        )
      },
    },
    {
      title: "Tiêu đề (Subject)",
      dataIndex: "subject",
      key: "subject",
      render: (subject: string, record: EmailLogItem) => (
        <div className="flex flex-col gap-0.5">
          <span className="line-clamp-2 font-medium text-gray-800">{subject}</span>
          {record.message_id && (
            <Text type="secondary" className="max-w-[280px] truncate font-mono text-[11px]">
              ID: {record.message_id}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Người gửi / Mailer",
      key: "sender",
      width: 180,
      render: (_: any, record: EmailLogItem) => (
        <div className="flex flex-col gap-1 text-xs">
          <span className="max-w-[160px] truncate text-gray-700">
            {record.from_address || <Text type="secondary">Mặc định</Text>}
          </span>
          <div>
            <Tag color="geekblue" className="m-0 font-mono text-[10px]">
              {record.mailer || "smtp"}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 130,
      align: "center",
      render: (status: string, record: EmailLogItem) => {
        const isSuccess = status === "sent"
        return (
          <div className="flex items-center justify-center gap-1">
            <Tag color={isSuccess ? "success" : "error"} className="m-0">
              {isSuccess ? "Thành công" : "Thất bại"}
            </Tag>
            {!isSuccess && record.error_message && (
              <Tooltip title={record.error_message}>
                <WarningOutlined className="text-rose-500 text-xs" />
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_: any, record: EmailLogItem) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            onViewDetail(record)
          }}
        >
          Xem
        </Button>
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<EmailLogItem>
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        onRow={(record) => ({
          onClick: () => onViewDetail(record),
          className: "cursor-pointer hover:bg-blue-50/40 transition-colors",
        })}
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

EmailLogTable.displayName = "EmailLogTable"

export default EmailLogTable
